import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/dateFormatter";
import {
  useInAppNotifications,
  useMarkNotificationsRead,
  useUnreadNotificationCount,
} from "@/hooks/notifications/in-app-notification.hook";
import type { InAppNotificationResponse } from "@/models/responses/in-app-notification.response";

export const NotificationsButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadCount } = useUnreadNotificationCount();
  const { data: notifications, isLoading } = useInAppNotifications({
    limit: 10,
  });
  const { mutate: markRead } = useMarkNotificationsRead();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: InAppNotificationResponse) => {
    if (!notification.readAt) {
      markRead([notification.id]);
    }
    setIsOpen(false);
    navigate(notification.linkPath);
  };

  const count = unreadCount?.count ?? 0;
  const items = notifications?.data ?? [];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setIsOpen((open) => !open)}
        className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-stroke bg-white transition-all duration-300 hover:scale-105 hover:bg-off-white cursor-pointer"
      >
        <img
          src="/assets/icons/notification-bell.svg"
          alt=""
          className="size-5"
          aria-hidden
        />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-clash-display font-semibold leading-none">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-lg border border-stroke bg-white shadow-lg z-50">
          <div className="px-4 py-3 border-b border-stroke">
            <p className="font-clash-display text-sm font-semibold text-medium-dark-grey">
              Notifications
            </p>
          </div>

          {isLoading ? (
            <p className="font-clash-display text-sm text-medium-grey text-center py-8">
              Loading...
            </p>
          ) : items.length === 0 ? (
            <p className="font-clash-display text-sm text-medium-grey text-center py-8">
              No notifications yet.
            </p>
          ) : (
            <div>
              {items.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b border-stroke last:border-b-0 hover:bg-off-white transition-colors",
                    !notification.readAt && "bg-baby-blue/40"
                  )}
                >
                  <p className="font-clash-display text-sm font-semibold text-medium-dark-grey">
                    {notification.title}
                  </p>
                  <p className="font-clash-display text-xs text-medium-grey mt-0.5 line-clamp-2">
                    {notification.body}
                  </p>
                  <p className="font-clash-display text-xs text-medium-grey mt-1">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
